import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
  Radar,
  Activity,
  Mic,
  FileText,
  Youtube,
  Layers,
  CheckCircle2,
  ShieldAlert,
  ArrowRight,
  Sparkles,
} from "lucide-react";
import { joinWaitlist } from "@/lib/api";
import { captureAttributionFromUrl, getAttribution } from "@/lib/attribution";

/* --- shared design tokens (kept inline so the landing page is self-contained) --- */
const EYEBROW =
  "font-mono text-[10px] uppercase tracking-[0.28em] text-[#64748b]";
const H2 = "text-2xl font-bold tracking-tight text-[#f8fafc] md:text-3xl";
const BODY = "text-sm leading-relaxed text-[#94a3b8]";

function WaitlistForm({ testIdPrefix = "hero", sourceNote = "" }) {
  const [email, setEmail] = useState("");
  const [state, setState] = useState("idle"); // idle | submitting | joined | already

  const onSubmit = async (e) => {
    e.preventDefault();
    const v = email.trim();
    if (!v) return;
    setState("submitting");
    try {
      const attribution = getAttribution();
      const r = await joinWaitlist(v, sourceNote, attribution);
      if (r?.status === "already_joined") {
        setState("already");
        toast.success("You're already on the list — we'll be in touch.");
      } else {
        setState("joined");
        toast.success("You're on the list. We'll email you when Signal Edge opens.");
      }
    } catch (err) {
      setState("idle");
      const msg =
        err?.response?.data?.detail || "Something went wrong. Try again in a moment.";
      toast.error(msg);
    }
  };

  if (state === "joined" || state === "already") {
    return (
      <div
        data-testid={`${testIdPrefix}-waitlist-success`}
        className="flex items-center gap-3 border border-[#10b981]/30 bg-[#10b981]/5 px-4 py-3"
      >
        <CheckCircle2 className="h-5 w-5 shrink-0 text-[#10b981]" />
        <div className="text-sm text-[#f8fafc]">
          {state === "already"
            ? "You're already on the waitlist."
            : "You're in. We'll email you the moment Signal Edge opens."}
        </div>
      </div>
    );
  }

  return (
    <form
      onSubmit={onSubmit}
      data-testid={`${testIdPrefix}-waitlist-form`}
      className="flex flex-col gap-2 sm:flex-row"
    >
      <input
        type="email"
        required
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        data-testid={`${testIdPrefix}-waitlist-email`}
        placeholder="you@example.com"
        className="min-w-0 flex-1 border border-[#1e293b] bg-[#111827] px-4 py-3 text-sm text-[#f8fafc] placeholder:text-[#64748b] focus:border-[#3b82f6] focus:outline-none focus:ring-1 focus:ring-[#3b82f6]"
      />
      <button
        type="submit"
        disabled={state === "submitting"}
        data-testid={`${testIdPrefix}-waitlist-submit`}
        className="inline-flex items-center justify-center gap-2 border border-[#3b82f6] bg-[#3b82f6] px-5 py-3 text-sm font-semibold text-white transition-colors hover:bg-[#2563eb] disabled:opacity-60"
      >
        {state === "submitting" ? "Joining…" : "Join the waitlist"}
        {state !== "submitting" && <ArrowRight className="h-4 w-4" />}
      </button>
    </form>
  );
}

function Eyebrow({ children, dot }) {
  return (
    <div className={`${EYEBROW} inline-flex items-center gap-2`}>
      {dot && (
        <span className="relative flex h-1.5 w-1.5">
          <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-[#10b981] opacity-75" />
          <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-[#10b981]" />
        </span>
      )}
      {children}
    </div>
  );
}

function Step({ n, icon: Icon, title, body }) {
  return (
    <div
      data-testid={`landing-step-${n}`}
      className="relative flex flex-col gap-3 border border-[#1e293b] bg-[#0f172a] p-6"
    >
      <div className="flex items-center gap-3">
        <div className="flex h-9 w-9 items-center justify-center border border-[#3b82f6]/40 bg-[#3b82f6]/10">
          <Icon className="h-4 w-4 text-[#3b82f6]" />
        </div>
        <div className="font-mono text-[10px] uppercase tracking-[0.24em] text-[#475569]">
          Step {n}
        </div>
      </div>
      <div className="text-base font-semibold text-[#f8fafc]">{title}</div>
      <div className={BODY}>{body}</div>
    </div>
  );
}

function PreviewCard() {
  // Static, non-interactive mock — visual only. Kept intentionally lightweight
  // so it renders without the API and can't be mistaken for live data.
  const voices = ["Chamath", "Pomp", "Melker", "Saylor"];
  return (
    <div
      data-testid="landing-preview-card"
      className="border border-[#1e293b] bg-[#0b1120]"
    >
      <div className="border-b border-[#1e293b] bg-[#0f172a] px-4 py-2 font-mono text-[10px] uppercase tracking-[0.24em] text-[#475569]">
        Sample convergence · illustrative
      </div>
      <div className="grid grid-cols-[64px_1fr_auto] items-center gap-4 px-5 py-5">
        <div className="font-mono text-2xl font-bold text-[#f8fafc]">01</div>
        <div>
          <div className="flex items-baseline gap-2">
            <span className="text-lg font-bold tracking-tight text-[#f8fafc]">
              MSTR
            </span>
            <span className="text-xs text-[#64748b]">MicroStrategy</span>
          </div>
          <div className="mt-1 flex flex-wrap gap-x-3 gap-y-1 text-xs text-[#94a3b8]">
            {voices.map((v) => (
              <span key={v} className="inline-flex items-center gap-1.5">
                <span className="h-1 w-1 rounded-full bg-[#3b82f6]" />
                {v}
              </span>
            ))}
          </div>
        </div>
        <div className="flex items-center gap-1">
          {[1, 2, 3, 4, 5].map((i) => (
            <span
              key={i}
              className={`h-2 w-2 rounded-full ${
                i <= 4 ? "bg-[#3b82f6]" : "bg-[#1e293b]"
              }`}
            />
          ))}
        </div>
      </div>
    </div>
  );
}

function PriceCard({ featured, eyebrow, price, period, save, features, note }) {
  return (
    <div
      data-testid={`landing-plan-${period.toLowerCase()}`}
      className={`relative flex flex-col gap-5 border p-6 ${
        featured
          ? "border-[#3b82f6] bg-[#0f172a] shadow-[0_0_40px_rgba(59,130,246,0.08)]"
          : "border-[#1e293b] bg-[#0f172a]"
      }`}
    >
      {featured && (
        <div className="absolute -top-3 left-6 border border-[#3b82f6] bg-[#3b82f6] px-2 py-0.5 font-mono text-[9px] uppercase tracking-[0.24em] text-white">
          Best value
        </div>
      )}
      <div>
        <div className={EYEBROW}>{eyebrow}</div>
        <div className="mt-3 flex items-baseline gap-1">
          <span className="text-4xl font-bold tracking-tight text-[#f8fafc]">
            {price}
          </span>
          <span className="text-sm text-[#64748b]">/{period}</span>
        </div>
        {save && (
          <div className="mt-1 text-xs text-[#10b981]">{save}</div>
        )}
      </div>
      <ul className="space-y-2 text-sm text-[#94a3b8]">
        {features.map((f) => (
          <li key={f} className="flex items-start gap-2">
            <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-[#3b82f6]" />
            <span>{f}</span>
          </li>
        ))}
      </ul>
      <div className="mt-auto text-xs text-[#64748b]">{note}</div>
    </div>
  );
}

function FaqItem({ q, a, testId }) {
  return (
    <details
      data-testid={testId}
      className="group border-b border-[#1e293b] py-4"
    >
      <summary className="flex cursor-pointer list-none items-center justify-between gap-4 text-sm font-semibold text-[#f8fafc] transition-colors group-open:text-[#3b82f6]">
        <span>{q}</span>
        <span className="ml-4 font-mono text-lg text-[#475569] group-open:hidden">+</span>
        <span className="ml-4 hidden font-mono text-lg text-[#475569] group-open:inline">–</span>
      </summary>
      <div className={`${BODY} mt-3`}>{a}</div>
    </details>
  );
}

export default function Landing() {
  // Snapshot attribution from ?utm_*= (and ?ref=) on mount so a fresh visit
  // with UTMs stashes them in localStorage (30-day TTL). Visitors who bounce
  // and come back later still get attributed to the original tweet/post.
  useEffect(() => {
    captureAttributionFromUrl();
  }, []);

  return (
    <div
      data-testid="landing-page"
      className="min-h-screen bg-[#0b1120] text-[#f8fafc]"
    >
      {/* Nav */}
      <header
        data-testid="landing-nav"
        className="sticky top-0 z-30 border-b border-[#1e293b] bg-[#0b1120]/95 backdrop-blur"
      >
        <div className="mx-auto flex max-w-[1200px] items-center justify-between px-5 py-4">
          <Link
            to="/"
            data-testid="landing-brand-link"
            className="flex items-center gap-2"
          >
            <div className="flex h-7 w-7 items-center justify-center border border-[#3b82f6]/40 bg-[#3b82f6]/10">
              <Radar className="h-4 w-4 text-[#3b82f6]" />
            </div>
            <div className="leading-tight">
              <div className="text-sm font-bold tracking-tight">SIGNAL EDGE</div>
              <div className="font-mono text-[9px] uppercase tracking-[0.2em] text-[#64748b]">
                convergence intel
              </div>
            </div>
          </Link>
          <div className="flex items-center gap-3">
            <a
              href="#pricing"
              data-testid="landing-nav-pricing"
              className="hidden text-xs font-medium text-[#94a3b8] transition-colors hover:text-[#f8fafc] sm:inline"
            >
              Pricing
            </a>
            <a
              href="#faq"
              data-testid="landing-nav-faq"
              className="hidden text-xs font-medium text-[#94a3b8] transition-colors hover:text-[#f8fafc] sm:inline"
            >
              FAQ
            </a>
            <Link
              to="/login"
              data-testid="landing-nav-signin"
              className="text-xs font-medium text-[#94a3b8] transition-colors hover:text-[#f8fafc]"
            >
              Sign in
            </Link>
            <a
              href="#waitlist"
              data-testid="landing-nav-cta"
              className="inline-flex items-center gap-1.5 border border-[#3b82f6] bg-[#3b82f6] px-3 py-1.5 text-xs font-semibold text-white transition-colors hover:bg-[#2563eb]"
            >
              Join waitlist
            </a>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section
        data-testid="landing-hero"
        className="border-b border-[#1e293b]"
      >
        <div className="mx-auto max-w-[1200px] px-5 py-16 md:py-24">
          <div className="grid gap-10 md:grid-cols-[1.1fr_1fr] md:items-center md:gap-14">
            <div>
              <Eyebrow dot>Early access · Pre-launch</Eyebrow>
              <h1 className="mt-4 text-4xl font-bold leading-[1.05] tracking-tight text-[#f8fafc] sm:text-5xl lg:text-6xl">
                Track what smart money is really talking about.
              </h1>
              <p className="mt-5 max-w-[560px] text-base leading-relaxed text-[#94a3b8]">
                Signal Edge listens to the voices retail investors actually
                follow — CEOs, analysts, macro strategists, crypto natives — and
                surfaces the moments <em>multiple independent voices</em> land
                on the same ticker. That's a convergence signal, and it's the
                one thing a spreadsheet can't tell you.
              </p>

              <div id="waitlist" className="mt-8 max-w-lg">
                <WaitlistForm testIdPrefix="hero" sourceNote="landing_hero" />
                <p className="mt-3 text-xs text-[#64748b]">
                  No spam. Zero commitment. We'll email you the moment early
                  access opens.
                </p>
              </div>

              <div className="mt-8 flex flex-wrap items-center gap-x-6 gap-y-2 font-mono text-[10px] uppercase tracking-[0.24em] text-[#475569]">
                <span className="inline-flex items-center gap-1.5">
                  <Youtube className="h-3.5 w-3.5" /> YouTube
                </span>
                <span className="inline-flex items-center gap-1.5">
                  <Mic className="h-3.5 w-3.5" /> Podcasts
                </span>
                <span className="inline-flex items-center gap-1.5">
                  <FileText className="h-3.5 w-3.5" /> SEC filings
                </span>
                <span className="inline-flex items-center gap-1.5">
                  <Activity className="h-3.5 w-3.5" /> Earnings calls
                </span>
              </div>
            </div>

            <div className="relative">
              <div className="absolute -inset-4 -z-10 bg-gradient-to-tr from-[#3b82f6]/10 via-transparent to-transparent blur-2xl" />
              <PreviewCard />
              <div className="mt-3 text-center font-mono text-[10px] uppercase tracking-[0.24em] text-[#475569]">
                Illustrative — not live data
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* How it works */}
      <section data-testid="landing-how" className="border-b border-[#1e293b]">
        <div className="mx-auto max-w-[1200px] px-5 py-16 md:py-20">
          <div className="mb-10 max-w-2xl">
            <Eyebrow>How it works</Eyebrow>
            <h2 className={`mt-3 ${H2}`}>
              Three stages. One signal that matters.
            </h2>
            <p className={`mt-3 ${BODY}`}>
              Everything you see on the feed is traceable back to a real,
              linkable source. If a quote can't be verified against a public
              recording or filing, it doesn't ship.
            </p>
          </div>
          <div className="grid gap-4 md:grid-cols-3">
            <Step
              n={1}
              icon={Mic}
              title="Ingest the voices you care about"
              body="Podcasts, YouTube channels, earnings-call transcripts, and SEC filings — pulled continuously from a curated roster of executives, analysts, and independent voices."
            />
            <Step
              n={2}
              icon={Sparkles}
              title="LLMs extract, humans verify"
              body="Every candidate quote is mapped to a ticker, sentiment-tagged, and voice-fingerprinted to prevent podcast co-host misattributions. Ambiguous mentions get dropped, not guessed."
            />
            <Step
              n={3}
              icon={Layers}
              title="Convergence surfaces automatically"
              body="When multiple independent voices land on the same ticker in the same window, the signal climbs a 1–5 dot scale. Insiders count less than outside analysts."
            />
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section
        id="pricing"
        data-testid="landing-pricing"
        className="border-b border-[#1e293b]"
      >
        <div className="mx-auto max-w-[1200px] px-5 py-16 md:py-20">
          <div className="mb-10 max-w-2xl">
            <Eyebrow>Pricing</Eyebrow>
            <h2 className={`mt-3 ${H2}`}>Simple. Priced for retail.</h2>
            <p className={`mt-3 ${BODY}`}>
              Two plans, no upsells, no seat pricing. Cancel anytime. Billing
              opens with early access — join the waitlist to lock in launch
              pricing.
            </p>
          </div>

          <div className="grid gap-4 md:grid-cols-2 md:gap-6">
            <PriceCard
              eyebrow="Monthly"
              price="$19.99"
              period="month"
              features={[
                "Live convergence feed with 1–5 signal scale",
                "Search by expert name or ticker",
                "Every mention linked to a verifiable source",
                "14-day free trial when early access opens",
              ]}
              note="Cancel anytime. Card required to start trial."
            />
            <PriceCard
              featured
              eyebrow="Annual · Save 17%"
              price="$199"
              period="year"
              save="Two months free vs monthly"
              features={[
                "Everything in monthly",
                "Priority email support",
                "Early access to Convergence Signals page",
                "Locked-in launch pricing",
              ]}
              note="Cancel anytime. Card required to start trial."
            />
          </div>

          <div className="mt-10 max-w-lg">
            <WaitlistForm testIdPrefix="pricing" sourceNote="landing_pricing" />
            <p className="mt-3 text-xs text-[#64748b]">
              You'll be first in line when billing opens. No card required to
              join the waitlist.
            </p>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section
        id="faq"
        data-testid="landing-faq"
        className="border-b border-[#1e293b]"
      >
        <div className="mx-auto max-w-[900px] px-5 py-16 md:py-20">
          <div className="mb-8">
            <Eyebrow>Frequently asked</Eyebrow>
            <h2 className={`mt-3 ${H2}`}>Answers before you commit.</h2>
          </div>
          <div>
            <FaqItem
              testId="faq-not-advice"
              q="Is Signal Edge investment advice?"
              a="No. Signal Edge is a research tool that surfaces publicly-made statements and links you back to the source. Nothing here is a recommendation to buy, sell, or hold anything. Do your own diligence."
            />
            <FaqItem
              testId="faq-voices"
              q="Which voices do you track?"
              a="A curated roster of executives, macro analysts, and independent investors whose commentary tends to move retail attention. The list grows as new voices meet our sourcing bar — every voice must have publicly-linkable material we can point back to."
            />
            <FaqItem
              testId="faq-convergence"
              q="What exactly is a 'convergence' signal?"
              a="When two or more independent voices publicly mention the same ticker in overlapping time windows, that ticker climbs the feed. Insider mentions (a CEO talking about their own company) are weighted lower than outside voices. The dot scale (1–5) reflects the weighted signal strength."
            />
            <FaqItem
              testId="faq-hallucination"
              q="How do you prevent LLM hallucinations or misattributions?"
              a="Two guards. First, a deterministic ticker-alias table filters LLM output — only pre-known ticker aliases pass through. Second, on multi-speaker podcasts we voice-fingerprint the audio segment against the expert's enrolled reference. If the speaker match is ambiguous, the mention is dropped."
            />
            <FaqItem
              testId="faq-trial"
              q="How does the free trial work?"
              a="14 days, card required upfront. You get full access from day one. Cancel any time during the trial and you won't be charged. If you cancel after billing starts, you keep access until the end of your paid period."
            />
            <FaqItem
              testId="faq-launch"
              q="When does Signal Edge launch?"
              a="Soon — we're finishing the last of the tracked-voice enrollments and wiring up user accounts and billing. Join the waitlist and you'll be the first to know the exact date. No fake countdowns, no urgency theater."
            />
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer data-testid="landing-footer" className="bg-[#0b1120]">
        <div className="mx-auto max-w-[1200px] px-5 py-10">
          <div className="flex flex-col gap-4 border border-[#1e293b] bg-[#0f172a] p-5 md:flex-row md:items-start md:gap-4">
            <ShieldAlert className="h-5 w-5 shrink-0 text-[#f59e0b]" />
            <div className="text-xs leading-relaxed text-[#94a3b8]">
              <div className="mb-1 font-mono text-[10px] uppercase tracking-[0.24em] text-[#f59e0b]">
                Disclaimer
              </div>
              Signal Edge is a research and information tool. Nothing on this
              site constitutes investment, financial, legal, or tax advice.
              Public statements are surfaced verbatim with links to their
              source; interpretation is yours alone. Past commentary is not
              indicative of future results. Do your own research before making
              any investment decision.
            </div>
          </div>
          <div className="mt-6 flex flex-col items-start justify-between gap-3 text-[10px] text-[#475569] md:flex-row md:items-center">
            <div className="font-mono uppercase tracking-[0.24em]">
              © 2026 Signal Edge · Convergence intel for retail
            </div>
            <div className="flex items-center gap-4">
              <a href="#waitlist" className="hover:text-[#94a3b8]">
                Waitlist
              </a>
              <a href="#pricing" className="hover:text-[#94a3b8]">
                Pricing
              </a>
              <a href="#faq" className="hover:text-[#94a3b8]">
                FAQ
              </a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
